import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import moment from "moment";
import { FC, useEffect } from "react";
import { Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import FileUpload from "../../../components/forms/FileUpload/FileUpload";
import RequiredInput from "../../../components/forms/InputHOC";
import useUsers from "../../../hooks/api-calls/useUsers";
import useVehicles from "../../../hooks/api-calls/useVehicles";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { RefuelService } from "../../../services/refuels/refuelService";
import { Refuel } from "../../../type/entities/refuel-type";
import RefuelDocumentsViewer from "./RefuelDocumentsViewer";

interface RefuelFormProps {
    setOpenModal: any;
    refetch: () => void;
    refuel?: Refuel | null;
    formRef?: any;
    vehicleId?: string;
}

const createSchema = (isEdit: boolean) => yup.object({
    vehicleId: yup.string().required('El vehículo es obligatorio'),
    userId: yup.string().required('El conductor es obligatorio'),
    liters: yup.number().required('Los litros son obligatorios').min(0.1, 'Los litros deben ser mayor a 0'),
    totalCost: yup.number().required('El costo total es obligatorio').min(0.01, 'El costo debe ser mayor a 0'),
    date: yup.date().required('La fecha de repostaje es obligatoria'),
    totalKilometers: yup.number().nullable().min(0, 'Los kilómetros totales no pueden ser negativos'),
    description: yup.string().nullable(),
    documents: isEdit 
        ? yup.array().of(yup.mixed()) 
        : yup.array().of(yup.mixed()).min(1, 'Debe subir al menos un documento').required('Los documentos son obligatorios'),
})

// Eliminamos fuelTypeOptions ya que no está en la entidad del backend

const RefuelForm: FC<RefuelFormProps> = ({ setOpenModal, refetch, refuel, formRef, vehicleId = null }) => {

    // STATES

    const service = new RefuelService();
    const isEdit = refuel !== null;

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getVehiclesList } = useVehicles({ not_sold: true });
    const { getUsersList } = useUsers();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: refuel?.id ?? '',
            vehicleId: vehicleId ? vehicleId : refuel?.vehicle?.id ?? '',
            userId: refuel?.user?.id ?? '',
            liters: refuel?.liters ?? '',
            totalCost: refuel?.totalCost ?? '',
            date: refuel?.date ? new Date(refuel.date.date) : new Date(),
            totalKilometers: refuel?.totalKilometers ?? '',
            description: refuel?.description ?? '',
            documents: [] as File[],
            photoCount: refuel?.photoCount ?? 0,
        },
        validationSchema: createSchema(isEdit),
        validateOnBlur: false,
        onSubmit: values => {
            const fixedValues = {
                ...values,
                date: values.date ? moment(values.date).format('YYYY-MM-DD') : '',
            }
            if (!isEdit) {
                handleCreate(fixedValues)
            } else {
                handleEdit(fixedValues)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO REPOSTAJE
     * @EN CREATE A NEW REFUEL
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            // Crear FormData para enviar archivos
            const formData = new FormData();
            
            // Agregar campos básicos
            formData.append('vehicleId', values.vehicleId);
            formData.append('userId', values.userId);
            formData.append('liters', values.liters.toString());
            formData.append('totalCost', values.totalCost.toString());
            formData.append('date', values.date);
            
            if (values.totalKilometers) {
                formData.append('totalKilometers', values.totalKilometers.toString());
            }
            
            if (values.description) {
                formData.append('description', values.description);
            }
            
            // Agregar documentos
            if (values.documents && values.documents.length > 0) {
                values.documents.forEach((file: File) => {
                    formData.append('documents[]', file);
                });
            }
            
            const response = await (await service.createRefuel(formData)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Repostaje creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN REPOSTAJE
     * @EN EDIT A REFUEL
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editRefuel(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Repostaje editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <CustomSelect
                            required
                            isSearchable
                            id={'vehicleId'}
                            label="Vehículo"
                            containerClassName='md:col-span-6'
                            value={{ value: formik.values.vehicleId, label: getVehiclesList()?.find((v: any) => v.value === formik.values.vehicleId)?.label }}
                            options={getVehiclesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('vehicleId', e?.value) }
                            }
                            error={formik.errors.vehicleId}
                            isDisabled={vehicleId !== null}
                        />

                        <CustomSelect
                            required
                            isSearchable
                            id={'userId'}
                            label="Conductor"
                            containerClassName='md:col-span-6'
                            value={{ value: formik.values.userId, label: getUsersList()?.find((u: any) => u.value === formik.values.userId)?.label }}
                            options={getUsersList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('userId', e?.value) }
                            }
                            error={formik.errors.userId}
                        />

                        <RequiredInput
                            id='liters'
                            label="Litros"
                            type="number"
                            step="0.01"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.liters}
                        />

                        <RequiredInput
                            id='totalCost'
                            label="Costo Total (€)"
                            type="number"
                            step="0.01"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.totalCost}
                        />

                        <RequiredInput
                            id='totalKilometers'
                            label="Kilómetros Totales"
                            type="number"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.totalKilometers}
                            required={false}
                        />

                        <div className='md:col-span-6'>
                            <span className="block text-sm font-medium text-gray-700">Fecha del repostaje<span className="text-red-500"> *</span></span>
                            <DatePicker
                                id='date'
                                label="Fecha del repostaje"
                                className='w-full'
                                popperPlacement="bottom-start"
                                selected={formik.values.date ? new Date(formik.values.date) : null}
                                onChange={(date: Date | null) => {
                                    formik.setFieldValue('date', date ? moment(date) : '');
                                }}
                                placeholderText="Seleccionar fecha"
                                dateFormat="dd/MM/yyyy"
                                locale={es}
                                error={formik.errors.date}
                                required
                            />
                        </div>

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        <div className="md:col-span-12">
                            <FileUpload
                                className="mb-2"
                                files={formik.values.documents}
                                onFilesChange={(files) => formik.setFieldValue('documents', files)}
                                title="Documentos del repostaje"
                                maxFiles={6 - formik.values?.photoCount}
                                maxSize={5242880} // 5MB
                                accept={{ 'image/*': ['jpg', 'jpeg', 'png', 'gif'] }}
                            />
                            {formik.errors.documents && (
                                 <div className="text-red-500 text-sm mt-1">
                                     {typeof formik.errors.documents === 'string' 
                                         ? formik.errors.documents 
                                         : 'Error en los documentos'
                                     }
                                 </div>
                             )}
                             
                             {/* Mostrar documentos existentes cuando se está editando */}
                             {isEdit && refuel?.id && (
                                 <div className="mt-4">
                                     <RefuelDocumentsViewer refuelId={refuel.id} />
                                 </div>
                             )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default RefuelForm;